import React, { useState, useEffect, useCallback, useContext } from "react";
import { useHistory, useParams } from "react-router-dom";
import { secureGet, secureGetPdf } from "../../config/api";
import { Helmet } from "react-helmet-async";
import { formatDateTime, formatAmount } from "../../utils/format";
import { useTranslation } from "react-i18next";
import ProductRow from "../../components/ProductRow";
import SnackbarNotification from '../../components/SnackbarNotification';
import Breadcrumbs from "../../components/Breadcrumbs";
import LoadingLogo from "../../components/LoadingLogo";
import UserContext from "../../Shared/UserContext";

import exportIcon from "../../icons/downloadBlue.svg";

const CreditNoteDetails = () => {
  const history = useHistory();
  const { id } = useParams();
  const { t } = useTranslation();
  const [creditNote, setCreditNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const { state: { role } } = useContext(UserContext);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchCreditNote = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await secureGet(`/creditNotes/${id}`, showSnackbar);
      setCreditNote(response.data);
      console.log("creditNote", creditNote);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Failed to fetch credit note:", error);
      }
      setCreditNote(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchCreditNote();
  }, [fetchCreditNote]);

  const handleDownloadClick = async () => {
    if (!creditNote?.id) return;
    try {
      const response = await secureGetPdf(`/creditNotes/ba/${creditNote.id}`, showSnackbar);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Credit_Note_${creditNote.creditNoteNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      showSnackbar(t('confirmation.downloadSuccess'), 'success');
    } catch (error) {
      console.error("Failed to download credit note:", error);
    }
  };

  const renderActionButtons = () => (
    <>
        <button
          className="flex items-center justify-center bg-white text-primary px-6 py-3 font-bold text-[14px] rounded-[12px] gap-x-2 shadow-[0px_20px_80px_#00000011]"
          onClick={handleDownloadClick}
        >
          <img src={exportIcon} alt="download" className="w-5 h-5" />
          {t('actions.downloadBA')}
        </button>
    </>
  );

  if (loading) return <LoadingLogo />;
  if (!creditNote) return null;

  return (
    <div className="h-full flex flex-col">
      <Helmet>
        <title>Drivnest - {t("titles.br_details")}</title>
      </Helmet>

      <div className="flex-shrink-0 pb-2">
      <div className="mb-4">
        <Breadcrumbs />
        <h1 className="text-[40px] font-semibold text-black">
          {t('titles.br_details')}
        </h1>

        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-[10px]">
            <p className="text-black font-semibold text-[24px]">
              {creditNote.creditNoteNumber}
            </p>
          </div>
          {/*<div className="flex space-x-4">{renderActionButtons()}</div>*/}
        </div>

        <div>
          <p className="text-[#38485C] text-[20px] font-semibold">
            {creditNote.client?.code} - {creditNote.client?.name}
          </p>
        </div>
      </div>

      <div className="w-full flex justify-between bg-white py-4 pl-[32px] rounded-[12px] shadow-[0px_6px_26px_#626B7D12] mb-4">
        <div className="flex-1">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">
            {t('rows.creationDate')}
          </p>
          <p className="text-[#38485C] text-[20px] font-semibold">
            {formatDateTime(creditNote.createdAt)}
          </p>
        </div>

        <div className="flex-1">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">
            CRÉÉ PAR
          </p>
          <p className="text-[#38485C] text-[20px] font-semibold">
            {creditNote.user?.firstName} {creditNote.user?.lastName}
          </p>
        </div>

        <div className="flex-1">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">
            TOTAL HT
          </p>
          <p className="text-[#38485C] text-[20px] font-semibold">
            {formatAmount(creditNote.priceHT)}
          </p>
        </div>

        <div className="flex-1">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">
            TOTAL TTC
          </p>
          <p className="text-[#38485C] text-[20px] font-semibold">
            {formatAmount(creditNote.priceTTC)}
          </p>
        </div>
      </div>
      </div>

      <div className="flex-1 overflow-y-auto">
      <h3 className="text-[16px] font-medium text-[#ACAFC6] mb-4">
        {t('fields_types.list')}
      </h3>

      {!creditNote.details || creditNote.details.length === 0 ? (
        <div className="text-center py-12 text-gray-500">{t('empty.products')}</div>
      ) : (
      <div className="space-y-4">
        {creditNote.details?.map((product, index) => (
          <ProductRow key={index} product={product} />
        ))}
      </div>
      )}
      </div>

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </div>
  );
};

export default CreditNoteDetails;